import { RoleData, aiUsageLevels, experienceLevels, industries } from '../data/roles.ts';
import { SkillData, getSkillPremium } from '../data/skills.ts';
import { locations } from '../data/locations.ts';

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function compound(value: number, rate: number, years: number): number {
  return value * Math.pow(1 + rate, years);
}

export function calculateNPV(cashFlows: number[], discountRate: number): number {
  return cashFlows.reduce((acc, flow, i) => acc + flow / Math.pow(1 + discountRate, i), 0);
}

export function solveIRR(cashFlows: number[]): number | null {
  if (cashFlows.length < 2) return null;
  let rate = 0.1;
  for (let i = 0; i < 100; i++) {
    const npv = calculateNPV(cashFlows, rate);
    const derivative = cashFlows.reduce((acc, flow, t) => acc - (t * flow) / Math.pow(1 + rate, t + 1), 0);
    if (Math.abs(npv) < 1e-6) return rate;
    if (Math.abs(derivative) < 1e-12) return null;
    rate = rate - npv / derivative;
    if (rate < -1) rate = -0.99;
    if (rate > 10) rate = 10;
  }
  return rate;
}

export interface SalaryGapInputs {
  roleId: string;
  industryId: string;
  experienceId: string;
  currentSalary: number;
  aiUsageId: string;
  years: number;
  locationId?: string;
}

export interface SalaryGapResult {
  currentPath: number[];
  aiPath: number[];
  year1Gap: number;
  year3Gap: number;
  year5Gap: number;
  cumulativeGap: number;
  aiExposureTier: string;
  topSkills: string[];
  effectivePremium: number;
  year5CurrentSalary: number;
  year5AiSalary: number;
}

export function calculateSalaryGap(
  role: RoleData,
  inputs: SalaryGapInputs
): SalaryGapResult {
  const industry = industries.find(i => i.id === inputs.industryId) || industries[0];
  const experience = experienceLevels.find(e => e.id === inputs.experienceId) || experienceLevels[1];
  const usage = aiUsageLevels.find(u => u.id === inputs.aiUsageId) || aiUsageLevels[0];
  const location = locations.find(l => l.id === inputs.locationId) || locations[0];

  const effectivePremium = Math.max(0.05, Math.min(0.75, role.basePremium * (1 + industry.modifier) * (1 + experience.modifier) * location.premiumMultiplier));
  const capture = usage.capture;
  const baselineGrowth = 0.03;

  const currentPath: number[] = [];
  const aiPath: number[] = [];
  let cumulativeGap = 0;

  for (let y = 1; y <= inputs.years; y++) {
    const currentSalary = compound(inputs.currentSalary, baselineGrowth + effectivePremium * capture, y);
    const aiSalary = compound(inputs.currentSalary, baselineGrowth + effectivePremium, y);
    currentPath.push(currentSalary);
    aiPath.push(aiSalary);
    cumulativeGap += (aiSalary - currentSalary);
  }

  return {
    currentPath,
    aiPath,
    year1Gap: aiPath[0] - currentPath[0],
    year3Gap: aiPath[2] - currentPath[2] || 0,
    year5Gap: aiPath[4] - currentPath[4] || 0,
    cumulativeGap,
    aiExposureTier: role.aiExposureTier,
    topSkills: role.topSkills,
    effectivePremium,
    year5CurrentSalary: currentPath[Math.min(4, currentPath.length - 1)],
    year5AiSalary: aiPath[Math.min(4, aiPath.length - 1)]
  };
}

export interface SkillsROIInputs {
  role: RoleData;
  skillId: string;
  learningPath: 'self-study' | 'bootcamp' | 'university';
  weeklyHours: number;
  currentSalary: number;
  remainingYears: number;
  locationId?: string;
}

export interface SkillsROIResult {
  timeToAcquireMonths: number;
  totalInvestment: number;
  annualPremium: number;
  lifetimeValue: number;
  roiPercent: number;
  paybackMonths: number;
  irr: number | null;
  comparison: string;
}

export function calculateSkillsROI(
  skill: SkillData,
  inputs: SkillsROIInputs
): SkillsROIResult {
  const hourlyRate = inputs.currentSalary / 2080;
  let hours = skill.hoursRequired;
  let cost = skill.courseCost;

  if (inputs.learningPath === 'bootcamp') {
    hours = skill.bootcampHours;
    cost = skill.bootcampCost;
  } else if (inputs.learningPath === 'university') {
    hours = skill.hoursRequired * 1.5;
    cost = skill.courseCost * 3;
  }

  const timeToAcquireWeeks = hours / Math.max(1, inputs.weeklyHours);
  const opportunityCost = timeToAcquireWeeks * 40 * hourlyRate * 0.5; // assume 50% productivity during study
  const totalInvestment = opportunityCost + cost;

  const location = locations.find(l => l.id === inputs.locationId) || locations[0];
  const premiumRate = getSkillPremium(skill.id, inputs.role.category) * location.premiumMultiplier;
  const annualPremium = inputs.currentSalary * premiumRate;
  const lifetimeValue = annualPremium * inputs.remainingYears;
  const roiPercent = totalInvestment > 0 ? ((lifetimeValue - totalInvestment) / totalInvestment) * 100 : 0;
  const paybackMonths = annualPremium > 0 ? totalInvestment / (annualPremium / 12) : 0;

  const cashFlows = [-totalInvestment];
  for (let i = 0; i < Math.min(10, inputs.remainingYears); i++) {
    cashFlows.push(annualPremium);
  }
  const irr = solveIRR(cashFlows);

  const comparison = annualPremium > 15000
    ? `This skill earns more than an MBA over 10 years for ${inputs.role.title}s`
    : annualPremium > 8000
      ? `This skill earns about as much as a part-time side hustle over 10 years`
      : `This skill provides modest but steady upside; consider stacking with another skill`;

  return {
    timeToAcquireMonths: timeToAcquireWeeks / 4.33,
    totalInvestment,
    annualPremium,
    lifetimeValue,
    roiPercent,
    paybackMonths,
    irr,
    comparison
  };
}

export interface ProductivityInputs {
  role: RoleData;
  hourlyRate: number;
  writingHours: number;
  researchHours: number;
  dataHours: number;
  emailHours: number;
  codeHours: number;
  adminHours: number;
  aiUsageLevel: number; // 0-1
}

export interface ProductivityResult {
  totalHoursSavedPerWeek: number;
  annualValue: number;
  fteEquivalent: number;
  tier: string;
  biggestOpportunity: string;
  breakdown: Record<string, number>;
}

export function calculateProductivity(inputs: ProductivityInputs): ProductivityResult {
  const multipliers: Record<string, number> = {
    writing: 3.8,
    research: 2.4,
    data: 2.1,
    email: 1.8,
    code: 2.9,
    admin: 1.5
  };

  const hours: Record<string, number> = {
    writing: inputs.writingHours,
    research: inputs.researchHours,
    data: inputs.dataHours,
    email: inputs.emailHours,
    code: inputs.codeHours,
    admin: inputs.adminHours
  };

  const labels: Record<string, string> = {
    writing: 'Writing & content',
    research: 'Research',
    data: 'Data analysis',
    email: 'Email & communication',
    code: 'Coding',
    admin: 'Admin & operations'
  };

  const breakdown: Record<string, number> = {};
  let totalHoursSaved = 0;
  let biggest = '';
  let biggestValue = 0;

  for (const key of Object.keys(hours)) {
    const saved = hours[key] * (1 - 1 / multipliers[key]) * inputs.aiUsageLevel;
    breakdown[labels[key]] = saved;
    totalHoursSaved += saved;
    if (saved > biggestValue) {
      biggestValue = saved;
      biggest = labels[key];
    }
  }

  const annualValue = totalHoursSaved * inputs.hourlyRate * 52;
  const fteEquivalent = totalHoursSaved * 52 / 2080;

  let tier = 'Beginner';
  if (inputs.aiUsageLevel >= 0.8) tier = 'Power user';
  else if (inputs.aiUsageLevel >= 0.5) tier = 'Advanced';
  else if (inputs.aiUsageLevel >= 0.2) tier = 'Developing';

  return {
    totalHoursSavedPerWeek: totalHoursSaved,
    annualValue,
    fteEquivalent,
    tier,
    biggestOpportunity: biggest,
    breakdown
  };
}

export interface HireVsAIInputs {
  role: RoleData;
  taskVolumePerYear: number;
  annualSalary: number;
  benefitsMultiplier: number;
  equipmentCost: number;
  aiSubscriptionMonthly: number;
  implementationHours: number;
  oversightHoursPerWeek: number;
  qualityAdjustment: number;
  userHourlyRate: number;
}

export interface HireVsAIResult {
  hireCost: number;
  aiCost: number;
  qualityAdjustedAICost: number;
  breakevenVolume: number;
  fiveYearHireCost: number;
  fiveYearAICost: number;
  fiveYearSavings: number;
  verdict: string;
}

export function calculateHireVsAI(inputs: HireVsAIInputs): HireVsAIResult {
  const hireCost = inputs.annualSalary * inputs.benefitsMultiplier + inputs.equipmentCost;
  const aiCost = inputs.aiSubscriptionMonthly * 12 + inputs.implementationHours * inputs.userHourlyRate + inputs.oversightHoursPerWeek * 52 * inputs.userHourlyRate;
  const qualityAdjustedAICost = aiCost / Math.max(0.3, inputs.qualityAdjustment);

  const costPerTaskHire = inputs.taskVolumePerYear > 0 ? hireCost / inputs.taskVolumePerYear : 0;
  const costPerTaskAI = inputs.taskVolumePerYear > 0 ? aiCost / inputs.taskVolumePerYear : 0;
  const breakevenVolume = costPerTaskAI > 0 && costPerTaskHire > 0 ? aiCost / (hireCost / inputs.taskVolumePerYear) : 0;

  let fiveYearHireCost = 0;
  for (let i = 0; i < 5; i++) {
    fiveYearHireCost += hireCost * Math.pow(1.03, i);
  }
  const fiveYearAICost = aiCost * 5;
  const fiveYearSavings = fiveYearHireCost - fiveYearAICost;

  const verdict = qualityAdjustedAICost < hireCost
    ? `For your volume and quality requirements, AI saves you ${formatCurrency(Math.max(0, hireCost - qualityAdjustedAICost))}/year`
    : `Hiring is the better ROI at your expected task volume`;

  return {
    hireCost,
    aiCost,
    qualityAdjustedAICost,
    breakevenVolume,
    fiveYearHireCost,
    fiveYearAICost,
    fiveYearSavings,
    verdict
  };
}

export interface AutomationInputs {
  employees: number;
  hoursPerWeek: number;
  hourlyCost: number;
  taskType: 'data-entry' | 'reporting' | 'customer-onboarding' | 'invoice-processing' | 'qa-testing';
  implementationCost: number;
  errorCost: number;
  errorReduction: number;
}

export interface AutomationResult {
  annualHoursSaved: number;
  laborSavings: number;
  errorSavings: number;
  totalAnnualBenefit: number;
  roiPercent: number;
  paybackMonths: number;
  fiveYearSavings: number;
  equivalentFTEs: number;
}

const automationEfficiency: Record<string, number> = {
  'data-entry': 0.85,
  'reporting': 0.80,
  'customer-onboarding': 0.70,
  'invoice-processing': 0.90,
  'qa-testing': 0.75
};

export function calculateAutomation(inputs: AutomationInputs): AutomationResult {
  const efficiency = automationEfficiency[inputs.taskType] || 0.8;
  const weeklyHoursSaved = inputs.employees * inputs.hoursPerWeek * efficiency;
  const annualHoursSaved = weeklyHoursSaved * 52;
  const laborSavings = annualHoursSaved * inputs.hourlyCost;
  const errorSavings = inputs.errorCost * inputs.errorReduction;
  const totalAnnualBenefit = laborSavings + errorSavings;
  const roiPercent = inputs.implementationCost > 0 ? ((totalAnnualBenefit - inputs.implementationCost) / inputs.implementationCost) * 100 : 0;
  const paybackMonths = totalAnnualBenefit > 0 ? inputs.implementationCost / (totalAnnualBenefit / 12) : 0;
  const fiveYearSavings = totalAnnualBenefit * 5;
  const equivalentFTEs = laborSavings / (inputs.hourlyCost * 2080 * 1.3);

  return {
    annualHoursSaved,
    laborSavings,
    errorSavings,
    totalAnnualBenefit,
    roiPercent,
    paybackMonths,
    fiveYearSavings,
    equivalentFTEs
  };
}

export interface DisplacementInputs {
  role: RoleData;
  experienceId: string;
  repetitiveTaskPercent: number;
  currentSalary: number;
  yearsToModel: number;
  locationId?: string;
}

export interface DisplacementResult {
  displacementRisk: number;
  yearsUntilDisruption: number;
  incomeAtRisk: number;
  wageCompressionSalary: number;
  statusQuoSalary: number;
  tasksAutomated: string[];
  handoffGap: number;
}

export function calculateDisplacement(inputs: DisplacementInputs): DisplacementResult {
  const experience = experienceLevels.find(e => e.id === inputs.experienceId) || experienceLevels[1];
  const location = locations.find(l => l.id === inputs.locationId) || locations[0];
  let experienceMod = 1;
  if (experience.seniorityLabel === 'entry') experienceMod = 1.15;
  else if (experience.seniorityLabel === 'senior') experienceMod = 0.8;

  const repetitive = Math.max(0, inputs.repetitiveTaskPercent - 50) / 10;
  const repetitiveMod = 1 + repetitive * 0.05;

  let risk = inputs.role.automationProbability * experienceMod * repetitiveMod * location.displacementRiskMultiplier;
  risk = Math.min(0.95, risk);

  const yearsUntilDisruption = risk > 0.7 ? 3 : risk > 0.5 ? 5 : risk > 0.3 ? 8 : 12;
  const incomeAtRisk = inputs.currentSalary * risk * inputs.yearsToModel;
  const statusQuoSalary = compound(inputs.currentSalary, inputs.role.baselineGrowth, inputs.yearsToModel);
  const wageCompressionSalary = statusQuoSalary * (1 - 0.25 * risk);

  const taskPool = [
    `${inputs.role.title.split(' ').slice(-1)[0]} report generation and summaries`,
    `Data entry and routine validation`,
    `Standard customer/client communications`,
    `Template-based document preparation`,
    `Basic research and fact gathering`
  ];
  const tasksAutomated = risk > 0.5 ? taskPool : taskPool.slice(0, 3);

  return {
    displacementRisk: risk,
    yearsUntilDisruption,
    incomeAtRisk,
    wageCompressionSalary,
    statusQuoSalary,
    tasksAutomated,
    handoffGap: incomeAtRisk * 0.4
  };
}

export interface LifetimeInputs {
  role: RoleData;
  currentAge: number;
  retirementAge: number;
  currentSalary: number;
  aiUpskillingCost: number;
  upskillingYears: number;
  locationId?: string;
}

export interface LifetimeResult {
  statusQuoNPV: number;
  aiUpskilledNPV: number;
  lifetimePremium: number;
  breakevenAge: number | null;
  trajectory: { age: number; statusQuo: number; ai: number }[];
}

export function calculateLifetimeEarnings(inputs: LifetimeInputs): LifetimeResult {
  const location = locations.find(l => l.id === inputs.locationId) || locations[0];
  const baselineGrowth = inputs.role.baselineGrowth;
  const aiPremiumGrowth = baselineGrowth + 0.04 * location.premiumMultiplier;
  const discountRate = 0.03;

  const trajectory: { age: number; statusQuo: number; ai: number }[] = [];
  const statusQuoFlows: number[] = [];
  const aiFlows: number[] = [];

  let cumulativeStatusQuo = 0;
  let cumulativeAI = -inputs.aiUpskillingCost;
  let breakevenAge: number | null = null;

  for (let age = inputs.currentAge; age <= inputs.retirementAge; age++) {
    const years = age - inputs.currentAge;
    const statusQuo = compound(inputs.currentSalary, baselineGrowth, years);
    const discountedStatusQuo = statusQuo / Math.pow(1 + discountRate, years);
    statusQuoFlows.push(discountedStatusQuo);
    cumulativeStatusQuo += statusQuo;

    let ai = statusQuo;
    if (years < inputs.upskillingYears) {
      ai = statusQuo * (1 - 0.05); // temporary productivity dip
    } else {
      ai = compound(inputs.currentSalary, aiPremiumGrowth, years) * 1.05;
    }
    const discountedAI = ai / Math.pow(1 + discountRate, years);
    aiFlows.push(discountedAI);
    cumulativeAI += ai;

    trajectory.push({ age, statusQuo, ai });

    if (breakevenAge === null && cumulativeAI > cumulativeStatusQuo && age > inputs.currentAge + inputs.upskillingYears) {
      breakevenAge = age;
    }
  }

  return {
    statusQuoNPV: statusQuoFlows.reduce((a, b) => a + b, 0),
    aiUpskilledNPV: aiFlows.reduce((a, b) => a + b, 0),
    lifetimePremium: aiFlows.reduce((a, b) => a + b, 0) - statusQuoFlows.reduce((a, b) => a + b, 0),
    breakevenAge,
    trajectory
  };
}
